import { Link, LinkProps } from '@tanstack/react-router';
import { CSSProperties, useState } from 'react';
import styled from 'styled-components';

import { Icon, IconButton, IconSource, Popover } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { borderWidth, color, cornerRadius, font, space } from '@creditornot/alchemy-react/tokens';
import { alchemyClass } from '@creditornot/alchemy-react/utils';
import { IconButton as CBIconButton } from '@creditornot/cb-button';
import { Settings } from '@creditornot/cb-icons';
import { useFeatureEnabled } from 'common/feature-flag/hooks/useFeatureEnabled';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUser } from 'common/hooks/useUser';

const Menu = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${space.regularXxlarge};
  min-height: 380px;
  padding: ${space.regularXxlarge};
`;

const SideNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${space.narrowSmall};
  min-width: 200px;
`;

const SideNavItem = styled.button`
  all: unset;
  border-radius: ${cornerRadius.medium};
  cursor: pointer;
  font: ${font.captionMedium};
  padding: ${space.regularMedium} ${space.regularLarge};
  transition: background-color 100ms linear;

  &:focus,
  &:hover {
    background-color: ${color.bgSurfaceTertiaryHovered};
  }

  &:focus-visible {
    outline: ${borderWidth.thickLarge} solid ${color.outlineFocused};
  }

  &[aria-selected='true'] {
    background-color: ${color.bgSurfaceTertiarySelected};
  }

  &:active {
    background-color: ${color.bgSurfaceTertiaryPressed};
  }
`;

const SettingsList = styled.div`
  border: ${borderWidth.regularMedium} solid ${color.borderSubdued};
  border-radius: ${cornerRadius.large};
  width: 340px;

  & > * ~ * {
    border-top: ${borderWidth.regularMedium} solid ${color.borderSubdued};
  }

  & > *:first-child {
    border-top-left-radius: calc(${cornerRadius.large} - ${borderWidth.regularMedium});
    border-top-right-radius: calc(${cornerRadius.large} - ${borderWidth.regularMedium});
  }

  & > *:last-child {
    border-bottom-left-radius: calc(${cornerRadius.large} - ${borderWidth.regularMedium});
    border-bottom-right-radius: calc(${cornerRadius.large} - ${borderWidth.regularMedium});
  }
`;

const SettingLink = styled(Link)`
  align-items: center;
  color: ${color.text};
  display: flex;
  gap: ${space.regularLarge};
  padding: ${space.regularXxlarge};
  transition: background-color 100ms linear;

  &:focus,
  &:hover {
    background-color: ${color.bgSurfaceSecondaryHovered};
  }

  &:focus-visible {
    outline: ${borderWidth.thickLarge} solid ${color.outlineFocused};
  }
`;

const SettingIconContainer = styled.div.attrs({ className: alchemyClass.elevation.xsmall })`
  align-items: center;
  border-radius: ${cornerRadius.large};
  border: ${borderWidth.regularMedium} solid ${color.borderSubdued};
  color: ${color.textSubdued};
  display: flex;
  flex-shrink: 0;
  height: 44px;
  justify-content: center;
  width: 44px;
`;

const SettingLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.narrowSmall};
`;

const SettingLabel = styled.div`
  font: ${font.captionMedium};
`;

const SettingDescription = styled.div`
  color: ${color.textSubdued};
  font: ${font.caption};
`;

type SideNavItem = 'order' | 'forecast' | 'vendor';

type NavItem = {
  id: SideNavItem;
  label: string;
  settings: {
    description: string;
    icon: IconSource;
    label: string;
    to: LinkProps['to'];
    visible: boolean;
  }[];
  visible: boolean;
};

type Props = {
  initialSetting?: SideNavItem;
  withCookbookTheme?: boolean;
};

export function SettingsMenu({ initialSetting = 'order', withCookbookTheme = false }: Props) {
  const countryCodes = useSelectedCountryCodes();

  const { hasRole } = useUser();
  const isDeveloper = hasRole('developer_wm');

  const productRelationshipsEnabled = useFeatureEnabled('product-relationships', countryCodes);

  const f = useFormatMessage();

  const [selectedNavItem, setSelectedNavItem] = useState<SideNavItem>(initialSetting);

  const navItems: NavItem[] = [
    {
      id: 'order',
      label: f('inventory_replenishment.settings_order_settings'),
      settings: [
        {
          description: f('inventory_replenishment.settings_automation_settings_description'),
          icon: icons.distance,
          label: f('inventory_replenishment.settings_automation_settings'),
          to: '/ordering-automation',
          visible: true,
        },
        {
          description: f('inventory_replenishment.settings_safety_stock_description'),
          icon: icons.grocery,
          label: f('inventory_replenishment.settings_safety_stock'),
          to: '/safety-stock-setup',
          visible: true,
        },
        {
          description: f('inventory_replenishment.settings_product_relationship_description'),
          icon: icons.barcodeViewfinder,
          label: f('inventory_replenishment.settings_product_relationships'),
          to: '/product-relationship',
          visible: isDeveloper || productRelationshipsEnabled,
        },
      ],
      visible: true,
    },
    {
      id: 'forecast',
      label: f('inventory_replenishment.settings_forecast_settings'),
      settings: [
        {
          description: f('inventory_replenishment.settings_forecast_description'),
          icon: icons.chartWaveform,
          label: f('inventory_replenishment.settings_forecast'),
          to: '/forecast-setup',
          visible: true,
        },
        {
          description: f('inventory_replenishment.settings_weekly_trends_description'),
          icon: icons.arrowUptrend,
          label: f('inventory_replenishment.settings_weekly_trends'),
          to: '/weekly-trends',
          visible: true,
        },
        {
          description: f('inventory_replenishment.settings_override_management_description'),
          icon: icons.squareLayers,
          label: f('inventory_replenishment.override_management'),
          to: '/override-management',
          visible: true,
        },
      ],
      visible: true,
    },
    {
      id: 'vendor',
      label: f('common.vendor'),
      settings: [
        {
          description: f('inventory_replenishment.purchase_order_budgets_description'),
          icon: icons.banknote,
          label: f('inventory_replenishment.purchase_order_budgets'),
          to: '/purchase-order-budgets',
          visible: true,
        },
      ],
      visible: true,
    },
  ] as const;

  const settings = navItems.find((item) => item.id === selectedNavItem)?.settings ?? [];

  return (
    <Popover
      offset={8}
      placement="bottom-end"
      renderArrow={false}
      style={{ '--radius': '24px' } as CSSProperties}
      trigger={
        withCookbookTheme ? (
          <CBIconButton
            aria-label={f('common.settings')}
            icon={Settings}
            size="small"
            variant="brand"
          />
        ) : (
          <IconButton
            aria-label={f('common.settings')}
            icon={icons.gear}
            size="small"
            variant="secondaryNeutral"
          />
        )
      }
    >
      <Menu>
        <SideNav>
          {navItems
            .filter(({ visible }) => visible)
            .map(({ id, label }) => (
              <SideNavItem
                aria-selected={id === selectedNavItem}
                key={id}
                onClick={() => setSelectedNavItem(id)}
                role="tab"
              >
                {label}
              </SideNavItem>
            ))}
        </SideNav>
        <SettingsList>
          {settings
            .filter(({ visible }) => visible)
            .map(({ description, icon, label, to }) => (
              <SettingLink key={to} to={to}>
                <SettingIconContainer>
                  <Icon size="small" source={icon} />
                </SettingIconContainer>
                <SettingLabelContainer>
                  <SettingLabel>{label}</SettingLabel>
                  <SettingDescription>{description}</SettingDescription>
                </SettingLabelContainer>
              </SettingLink>
            ))}
        </SettingsList>
      </Menu>
    </Popover>
  );
}
